import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData, View } from 'react-native';
import { translate } from '../../../services/translate';
import DeleteModal from '../../../components/DeleteModal';
import Toast from 'react-native-toast-message';
import { Pagination } from '../../../components/Pagination';
import { cameraService } from '../../../services/central-api/cameras';
import { ClientError } from '../../../services/central-api/base-service';
import { CamerasParamList } from '../../../typings/Params';
import { NavigationProp, RouteProp, useIsFocused, useNavigation, useRoute } from '@react-navigation/native';
import MyDataTable from '../../../components/MyDataTable';
import FilterInput from '../../../components/Filter/components/FilterInput';
import CustomButton from '../../../components/CustomButton';
import useFilters from '../../../components/Filter/hooks/useFilters';
import Filter from './Filters';
import DropdownButton from '../../../components/DropdownButton';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useAuth } from '../../../context/Auth';
import { useQuery } from '@tanstack/react-query';
import { useStyles } from 'react-native-unistyles';
import Tooltip from '../../../components/Tooltip';
import Badge from '../../../components/Badge';
import MyAppText from '../../../components/MyAppText';
import getBestContrastColor from '../../../services/best-contrast-color';

export default function AlertGroups() {
    const { theme } = useStyles();
    const route = useRoute<RouteProp<CamerasParamList, 'AlertGroups'>>();
    const navigation = useNavigation<NavigationProp<CamerasParamList>>();
    const { user: authenticatedUser } = useAuth();

    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [selectedAlertGroup, setSelectedAlertGroup] = useState<AlertGroupList>();
    const [tagsTooltipHeight, setTagsTooltipHeight] = useState<{ [key: number]: number; }>({});

    const [filters, setFilters] = useFilters<CamerasParamList['AlertGroups']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['camerasAlertGroups', filters],
        queryFn: () => cameraService.getAlertGroups(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    function showDeleteConfirmationDialog(alertGroup: AlertGroupList) {
        setSelectedAlertGroup(alertGroup);
        setDeleteModalVisible(true);
    }

    async function confirmDelete(alertGroup: AlertGroupList) {
        try {
            await cameraService.deleteAlertGroup(alertGroup.id);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            setDeleteModalVisible(false);
            refetch();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function onLayoutTooltipContent(alertGroupId: number, event: { nativeEvent: { layout: { x: number; y: number; width: number; height: number; }; }; }) {
        setTagsTooltipHeight(prev => ({
            ...prev,
            [alertGroupId]: event.nativeEvent.layout.height
        }));
    }

    return (
        <Container>
            <Header
                title={translate('alertGroups')}
                actions={<CustomButton text={translate('register')} onPress={() => {
                    navigation.navigate('UpsertAlertGroupModal');
                }} />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredAlertGroups')}
                    zIndex={2}
                    filter={<Filter setTextFilter={setTextFilter} textFilter={textFilter} filters={filters} setFilters={setFilters} />}
                    filterInput={<FilterInput
                        value={textFilter}
                        onChange={setTextFilter}
                        placeholder={translate('typeToSearch')}
                        onKeyPress={handleKeyDown}
                    />}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: translate('zone'),
                                selector: row => row.name,
                                wrap: true,
                                grow: 3
                            },
                            {
                                name: translate('cameraCount'),
                                selector: row => row.cameraCount || 0,
                                wrap: true,
                                grow: 1,
                                center: true,
                            },
                            {
                                name: translate('tags'),
                                cell: (row, rowIndex) =>
                                    row.tags && row.tags.length > 0 ? <Tooltip
                                        useSimpleBox
                                        style={{
                                            color: theme.colors.white['500'],
                                            padding: 16,
                                            borderRadius: 12,
                                            justifyContent: 'center',
                                            zIndex: 9999,
                                            width: row.tags.length >= 6 ? 450 : 150,
                                            background: theme.colors.neutral['500'],
                                            top: rowIndex > (filters.limit - 5) ? -tagsTooltipHeight[row.id] - 12 : 0
                                        }}
                                        tooltipContent={<View onLayout={(event) => onLayoutTooltipContent(row.id, event)} style={{ flexDirection: 'row', flexWrap: 'wrap', width: '100%', gap: 5 }}>
                                            {row.tags?.map(tag => (
                                                <Badge mode='ROUNDED' key={tag.id} backgroundColor={tag.color} size='small' textColor={getBestContrastColor(tag.color)}>{tag.name}</Badge>
                                            ))}
                                        </View>}
                                        tooltipDirection='left'
                                    >
                                        <MyAppText
                                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                            // @ts-ignore
                                            style={{ cursor: 'pointer', fontWeight: '600', fontSize: 14 }}
                                        >
                                            {translate('viewTags').toLowerCase()}
                                        </MyAppText>
                                    </Tooltip> : null,
                                wrap: true,
                                grow: 0,
                            },
                            {
                                button: true,
                                cell: row =>
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => navigation.navigate('UpsertAlertGroupModal', { id: row.id })
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => showDeleteConfirmationDialog(row),
                                            }
                                        ]}
                                    />
                            }
                        ]}
                        data={data?.rows ?? []}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        totalItems={data?.count ?? 0}
                        currentPage={filters.page}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                    />}
                />
            </Card>

            {selectedAlertGroup && <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedAlertGroup.name}
                onSubmit={async () => {
                    if (selectedAlertGroup) {
                        await confirmDelete(selectedAlertGroup);
                    }
                }}
            />}
        </Container>
    );
}
